import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { palette } from '@app/src/theme/palette'
import { SortOrder } from '@app/src/types/filter'
import type { Response } from '@app/src/types/resourceExplorer'
import { reporting } from '@app/src/wf-constants/paths'
import { AutoAwesomeOutlined, EditOutlined, HelpOutlineOutlined } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material'
import qs from 'qs'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'

interface AssessmentScreenedBannerProps {
  periodName: string
  questionnaireTemplateId: number
}

const AssessmentScreenedBanner: React.FC<AssessmentScreenedBannerProps> = ({ periodName, questionnaireTemplateId }) => {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const [dialogOpen, setDialogOpen] = useState(false)
  const theme = useTheme()

  const handleDialogOpen = () => setDialogOpen(true)
  const handleDialogClose = () => setDialogOpen(false)

  const { items: responses, isLoading } = useFetchCollectionWithPost<Response>({
    key: [FetchKey.Response, periodName, questionnaireTemplateId],
    endpoint: endpoints.responses,
    payload: {
      filter: [
        {
          name: 'request.periodName',
          filters: [{ operator: Operators.EqualTo, value: periodName }],
        },
        {
          name: 'request.questionnaireTemplateId',
          filters: [{ operator: Operators.EqualTo, value: questionnaireTemplateId }],
        },
        {
          name: 'isScraped',
          filters: [{ operator: Operators.EqualTo, value: true }],
        },
      ],
      include: [],
      sort: { target: 'submittedAt', order: SortOrder.DESCENDING },
      pagination: { pageNumber: 1, itemsPerPage: 1 },
    },
    options: {
      enabled: Boolean(periodName && questionnaireTemplateId),
      staleTime: ONE_MINUTE_MS,
    },
  })

  const handleUpdateClick = () => {
    if (responses?.[0]) {
      history.push({
        pathname: reporting,
        search: qs.stringify({
          requestId: responses[0].targetRequestId,
          responseId: responses[0].id,
        }),
      })
    }
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          bgcolor: palette.communication.main,
          py: 3,
        }}
      >
        <CircularProgress color="inherit" />
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          bgcolor: palette.communication.main,
          py: 4,
        }}
      >
        <Box
          sx={{
            maxWidth: 800,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              gap: 2,
            }}
          >
            <Avatar variant="rounded" sx={{ bgcolor: palette.communication.dark }}>
              <AutoAwesomeOutlined sx={{ color: 'common.white' }} />
            </Avatar>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={theme.typography.fontWeightMedium} sx={{ fontSize: 16 }}>
                {formatMessage({ id: 'assessment.alert.title' })}
              </Typography>
              <Typography sx={{ mb: 2 }} fontWeight={theme.typography.fontWeightMedium}>
                {formatMessage({ id: 'assessment.alert.description' })}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', gap: 2 }}>
            <Button startIcon={<HelpOutlineOutlined />} size="large" onClick={handleDialogOpen}>
              {formatMessage({ id: 'general.about' })}
            </Button>
            <Button
              variant="contained"
              startIcon={<EditOutlined />}
              size="large"
              color="primary"
              onClick={handleUpdateClick}
              disabled={!responses?.length}
            >
              {formatMessage({ id: 'general.update' })}
            </Button>
          </Box>
        </Box>
      </Box>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{formatMessage({ id: 'general.about' })}</DialogTitle>
        <DialogContent>
          <Typography>{formatMessage({ id: 'assessment.alert.dialogDescription' })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            {formatMessage({ id: 'general.close' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AssessmentScreenedBanner
